import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { message } from 'antd'

import { readCookieValue } from '@/helpers/cookies'
import { apiRequest, clearAuthSessionCookies, ensureSidCookieForAuthFlow, setNewApiToken } from '@/helpers/request'
import { profile } from '@/profile'
import type { T_UniRes } from '@/types'
import { formatAnyInputToPhoneMask } from '@/components/input/helpers'
import { isDataWrongAuthError, isOfflineAuthError, presentAuthServerMessage, showOfflineAuthMessage } from '../helpers'
import { useAuthStore } from '../../store'
import { savePaidCallSession } from '../../paid-call-session'
import {
  authSignPayload,
  createPaidOperationId,
  generateAuthKeyPair,
  isAllowedDifficulty,
  isWebCryptoAvailable,
  readCaptchaChallenge,
  signAuthPayload,
  solvePow,
  toCanonicalRuMobile,
} from '../authorize-ticket'
import type { T_AuthAttempt, T_AuthCaptchaChallenge, T_AuthSecurity } from '../authorize-ticket'


type T_ChallengeData = {
  ticket_id?: string
  nonce?: string
  difficulty?: number
  expires_in?: number
  timeout?: number
}

type T_AuthorizeSecurity = {
  request_id: string
  ticket_id?: string
  nonce?: string
  counter?: number
  public_key?: string
  signature?: string
  captcha_token?: string
}

type T_CreateData = {
  address?: string
  message?: string | number
  confirm?: boolean
  timeout?: number
}

type T_CaptchaWaiter = {
  resolve: (token: string) => void
  reject: (error: Error) => void
}

type T_ContinueResult =
  | { kind: 'invalid-phone' }
  | { kind: 'no-crypto' }
  | { kind: 'timeout', timeout: number }
  | { kind: 'message', code?: string, message?: string, details?: string }
  | {
    kind: 'api'
    res: T_UniRes<T_CreateData>
    ticketId: string | null
    security: T_AuthSecurity | null
  }

const BROWSER_UNSUPPORTED_MESSAGE = 'Этот браузер не поддерживает вход. Обновите браузер и попробуйте снова'
const CAPTCHA_FAILED_MESSAGE = 'Не удалось показать проверку. Попробуйте ещё раз'
const CHALLENGE_LIMIT_MESSAGE = 'Не удалось подготовить вход, повторите попытку через минуту'
const CHALLENGE_MISSING_MESSAGE = 'Не удалось начать вход. Попробуйте ещё раз'

type T_PreparedChallenge =
  | { status: 'ready', issued: T_UniRes<T_ChallengeData> }
  | { status: 'captcha', issued: T_UniRes<T_ChallengeData> }
  | { status: 'timeout', timeout: number }
  | { status: 'error', code?: string, message?: string, details?: string }

const isUsableChallenge = (
  value: T_PreparedChallenge,
): value is Extract<T_PreparedChallenge, { status: 'ready' | 'captcha' }> => (
  value.status === 'ready' || value.status === 'captcha'
)

const formatTimeoutMessage = (timeoutSeconds: number) => {
  const minutes = Math.floor(timeoutSeconds / 60)
  const seconds = timeoutSeconds % 60
  const timeString = `${minutes > 0 ? `${minutes} мин ` : ''}${seconds} сек`

  return `Подождите ${timeString} перед повторной попыткой входа`
}

const readTimeout = (data: unknown) => {
  if (!data || typeof data !== 'object' || !('timeout' in data)) return null

  const timeoutSeconds = Number((data as { timeout: unknown }).timeout)
  if (!Number.isFinite(timeoutSeconds)) return null

  return timeoutSeconds
}

const readServerDetails = (value: unknown) => {
  if (!value || typeof value !== 'object') return undefined
  const details = (value as { details?: unknown }).details
  return typeof details === 'string' ? details : undefined
}

const authorizeForm = () => (
  profile.isRegistrationSpecific
    ? 'client/visitor/authorize_create_vsem'
    : 'client/visitor/authorize_create_site'
)

const preparedChallengeFailure = (value: T_PreparedChallenge): T_ContinueResult | null => {
  if (value.status === 'timeout') return { kind: 'timeout', timeout: value.timeout }
  if (value.status === 'error') {
    return {
      kind: 'message',
      code: value.code,
      message: value.message || CHALLENGE_MISSING_MESSAGE,
      details: value.details,
    }
  }

  return null
}

const requestPreparedChallenge = async (): Promise<T_PreparedChallenge> => {
  await ensureSidCookieForAuthFlow()
  const issued = await apiRequest<T_UniRes<T_ChallengeData>>({
    method: 'client/security/challenge',
  })

  if (issued?.code === 'data-timeout') {
    const timeout = readTimeout(issued.data)
    if (timeout === null) {
      return {
        status: 'error',
        code: issued.code,
        message: issued.message,
        details: readServerDetails(issued),
      }
    }
    return { status: 'timeout', timeout }
  }

  if (issued?.code === 'protection-captcha-required') {
    return { status: 'captcha', issued }
  }

  const ticketId = issued?.data?.ticket_id
  const nonce = issued?.data?.nonce
  const difficulty = Number(issued?.data?.difficulty)

  if (issued?.code !== 'ok' || !ticketId || !nonce || !isAllowedDifficulty(difficulty)) {
    return {
      status: 'error',
      code: issued?.code,
      message: issued?.message,
      details: readServerDetails(issued),
    }
  }

  return { status: 'ready', issued }
}

export const useGetPhoneToCallMutation = () => {
  const userPhone = useAuthStore((s) => s.userPhone)
  const setOutgoingPhone = useAuthStore((s) => s.setOutgoingPhone)
  const setOutgoingMessageNumber = useAuthStore((s) => s.setOutgoingMessageNumber)
  const setCurrentStep = useAuthStore((s) => s.setCurrentStep)
  const setTicketId = useAuthStore((s) => s.setTicketId)
  const setAuthSecurity = useAuthStore((s) => s.setAuthSecurity)

  const inFlightRef = useRef(false)
  const attemptRef = useRef<T_AuthAttempt | null>(null)
  const preparedChallengeRef = useRef<Promise<T_PreparedChallenge> | null>(null)
  const captchaWaiterRef = useRef<T_CaptchaWaiter | null>(null)
  const captchaRefreshRef = useRef<(() => Promise<T_AuthCaptchaChallenge>) | null>(null)
  const [ captcha, setCaptcha ] = useState<T_AuthCaptchaChallenge | null>(null)

  const waitForCaptcha = (
    challenge: T_AuthCaptchaChallenge,
    refresh: () => Promise<T_AuthCaptchaChallenge>,
  ) => {
    captchaRefreshRef.current = refresh
    setCaptcha(challenge)

    return new Promise<string>((resolve, reject) => {
      captchaWaiterRef.current = { resolve, reject }
    })
  }

  const finishCaptchaWait = () => {
    captchaWaiterRef.current = null
    captchaRefreshRef.current = null
    setCaptcha(null)
  }

  const onCaptchaToken = useCallback((token: string) => {
    const waiter = captchaWaiterRef.current
    captchaWaiterRef.current = null
    captchaRefreshRef.current = null
    setCaptcha(null)
    waiter?.resolve(token)
  }, [])

  const onCaptchaFail = useCallback((reason?: string) => {
    const waiter = captchaWaiterRef.current
    captchaWaiterRef.current = null
    captchaRefreshRef.current = null
    setCaptcha(null)
    waiter?.reject(new Error(reason || 'captcha-failed'))
  }, [])

  const onCaptchaRefresh = useCallback(async () => {
    const refresh = captchaRefreshRef.current
    if (!refresh) throw new Error('refresh')
    return refresh()
  }, [])

  const currentStep = useAuthStore((s) => s.currentStep)

  useEffect(() => {
    if (currentStep !== 'initialScreen') return

    let cancelled = false

    const promise = requestPreparedChallenge()

    preparedChallengeRef.current = promise
    promise.catch(() => {
      if (cancelled) return
    })

    return () => {
      cancelled = true
    }
  }, [ currentStep ])

  const {
    mutate: getPhoneToCallMutation,
    isPending: isGetPhoneToCallPending,
  } = useMutation({
    mutationFn: async (): Promise<T_ContinueResult> => {
      setNewApiToken(null)
      setOutgoingMessageNumber(null)
      await ensureSidCookieForAuthFlow()
      const parentCookie = readCookieValue('parent')
      const mobile = toCanonicalRuMobile(userPhone)

      if (!mobile) return { kind: 'invalid-phone' }

      let ticketRetried = false

      const takeFreshTicket = async () => {
        ticketRetried = true
        const next = await requestPreparedChallenge()
        preparedChallengeRef.current = Promise.resolve(next)
        return next
      }

      let prepared = await preparedChallengeRef.current

      if (!prepared) return { kind: 'message', message: CHALLENGE_MISSING_MESSAGE }

      if (prepared.status === 'error' && isDataWrongAuthError(prepared.code, prepared.message) && !ticketRetried) {
        try {
          prepared = await takeFreshTicket()
        } catch {
          // Keep the original error result to avoid silent failures in UI.
        }
      }

      const blocked = preparedChallengeFailure(prepared)
      if (blocked) return blocked
      if (!isUsableChallenge(prepared)) {
        return { kind: 'message', message: CHALLENGE_MISSING_MESSAGE }
      }

      const authorizeWithTicket = async (
        current: Extract<T_PreparedChallenge, { status: 'ready' | 'captcha' }>,
      ): Promise<T_ContinueResult> => {
        const operationId = createPaidOperationId()

        const createSession = (
          security: T_AuthorizeSecurity,
          captchaToken?: string,
        ) => {
          const param: Record<string, unknown> = {
            type: 'wcallq',
            mobile,
            place: 'portal',
            form: authorizeForm(),
            security: captchaToken
              ? { ...security, captcha_token: captchaToken }
              : security,
          }

          if (parentCookie) param.parent = parentCookie

          return apiRequest<T_UniRes<T_CreateData>>({
            method: 'client/visitor/authorize_create',
            param,
          })
        }

        const readNextCaptcha = async (
          request: () => Promise<{ code?: string, data?: unknown } | null | undefined>,
        ) => {
          const next = await request()
          if (next?.code !== 'protection-captcha-required') throw new Error('refresh')

          const picture = readCaptchaChallenge(next.data)
          if (!picture) throw new Error('refresh')

          return picture
        }

        const takeCaptchaToken = async (
          data: unknown,
          fallbackMessage: string | undefined,
          refresh: () => Promise<T_AuthCaptchaChallenge>,
        ) => {
          const challenge = readCaptchaChallenge(data)

          if (!challenge) {
            return { kind: 'message' as const, message: fallbackMessage }
          }

          try {
            const token = await waitForCaptcha(challenge, refresh)
            return { kind: 'token' as const, token }
          } catch (error) {
            const text = error instanceof Error ? error.message : ''
            const isUserMessage = text.length > 0 && text !== 'captcha-failed'
            return { kind: 'message' as const, message: isUserMessage ? text : CAPTCHA_FAILED_MESSAGE }
          }
        }

        if (!isWebCryptoAvailable()) {
          if (current.status === 'captcha') {
            const captchaResult = await takeCaptchaToken(
              current.issued.data,
              current.issued.message,
              () => readNextCaptcha(async () => {
                throw new Error('refresh')
              }),
            )

            if (captchaResult.kind === 'message') return captchaResult

            const res = await createSession({
              request_id: operationId,
              ticket_id: current.issued.data?.ticket_id,
              nonce: current.issued.data?.nonce,
            }, captchaResult.token)

            return {
              kind: 'api',
              res,
              ticketId: current.issued.data?.ticket_id ?? null,
              security: null,
            }
          }

          return { kind: 'no-crypto' }
        }

        const signChallenge = async (issued: T_UniRes<T_ChallengeData>) => {
          const ticketId = issued.data?.ticket_id
          const nonce = issued.data?.nonce
          const difficulty = Number(issued.data?.difficulty)

          if (issued.code !== 'ok' || !ticketId || !nonce || !isAllowedDifficulty(difficulty)) {
            return null
          }

          const counter = await solvePow(nonce, mobile, difficulty)
          if (!counter) return { exhausted: true as const }

          const key = await generateAuthKeyPair()
          const payload = authSignPayload({
            ticketId,
            nonce,
            mobile,
            counter,
            publicKey: key.publicKey,
          })
          const signature = await signAuthPayload(key.privateKey, payload)
          const attempt: T_AuthAttempt = {
            mobile,
            operationId,
            ticketId,
            nonce,
            counter,
            publicKey: key.publicKey,
            signature,
            privateKey: key.privateKey,
          }

          attemptRef.current = attempt

          return { exhausted: false as const, attempt }
        }

        if (current.status !== 'ready') {
          return { kind: 'message', message: CHALLENGE_MISSING_MESSAGE }
        }

        const signed = await signChallenge(current.issued)

        if (!signed) {
          return { kind: 'message', message: current.issued.message || CHALLENGE_MISSING_MESSAGE }
        }

        if (signed.exhausted) {
          return { kind: 'message', message: CHALLENGE_LIMIT_MESSAGE }
        }

        const signedAttempt = signed.attempt

        const security: T_AuthSecurity = {
          request_id: signedAttempt.operationId,
          ticket_id: signedAttempt.ticketId,
          nonce: signedAttempt.nonce,
          counter: Number(signedAttempt.counter),
          public_key: signedAttempt.publicKey,
          signature: signedAttempt.signature,
        }

        try {
          let res = await createSession(security)

          if (res?.code === 'protection-captcha-required') {
            const captchaResult = await takeCaptchaToken(
              res.data,
              res.message,
              () => readNextCaptcha(() => createSession(security)),
            )

            if (captchaResult.kind === 'message') return captchaResult

            res = await createSession(security, captchaResult.token)
          }

          return {
            kind: 'api',
            res,
            ticketId: signedAttempt.ticketId,
            security,
          }
        } finally {
          attemptRef.current = null
          finishCaptchaWait()
        }
      }

      let result = await authorizeWithTicket(prepared)

      if (result.kind === 'api' && isDataWrongAuthError(result.res?.code, result.res?.message) && !ticketRetried) {
        try {
          const next = await takeFreshTicket()
          const nextBlocked = preparedChallengeFailure(next)
          if (nextBlocked) return nextBlocked
          if (!isUsableChallenge(next)) {
            return { kind: 'message', message: CHALLENGE_MISSING_MESSAGE }
          }

          result = await authorizeWithTicket(next)
        } catch {
          return result
        }
      }

      return result
    },

    onSuccess: (result) => {
      if (result.kind === 'invalid-phone') {
        message.error('Введите номер телефона')
        return
      }

      if (result.kind === 'no-crypto') {
        message.error(BROWSER_UNSUPPORTED_MESSAGE)
        return
      }

      if (result.kind === 'timeout') {
        message.error(formatTimeoutMessage(result.timeout))
        return
      }

      if (result.kind === 'message') {
        message.error(presentAuthServerMessage(result.message, result.code, result.details) || 'Неизвестная ошибка')
        return
      }

      const res = result.res

      if (res?.code === 'ok') {
        const address = res.data?.address
        const confirm = res.data?.confirm
        const outgoingMessageNumberRaw = res.data?.message ?? null
        const outgoingMessageNumber = outgoingMessageNumberRaw !== null
          ? String(outgoingMessageNumberRaw)
          : null

        if (!address) {
          clearAuthSessionCookies()
          setOutgoingMessageNumber(null)

          if (confirm === true) {
            message.error('Мы не смогли получить контактный номер для звонка, пожалуйста, повторите попытку через 2 минуты')
            return
          }

          message.error('Не удалось выполнить вход. Пожалуйста, закройте браузер, откройте сайт в новой вкладке браузера и попробуйте снова')
          return
        }

        if (result.ticketId) setTicketId(result.ticketId)
        if (result.security) setAuthSecurity(result.security)

        const shownPhone = formatAnyInputToPhoneMask(address)
        setOutgoingPhone(shownPhone)
        setOutgoingMessageNumber(outgoingMessageNumber)
        savePaidCallSession({
          step: 'outgoingCall',
          userPhone,
          outgoingPhone: shownPhone,
          outgoingMessageNumber,
          ticketId: result.ticketId,
          authSecurity: result.security,
        })
        setCurrentStep('outgoingCall')
        return
      }

      const timeout = res?.code === 'data-timeout' ? readTimeout(res.data) : null

      if (timeout !== null) {
        message.error(formatTimeoutMessage(timeout))
        return
      }

      setOutgoingMessageNumber(null)
      message.error(
        presentAuthServerMessage(
          res?.message,
          res?.code,
          readServerDetails(res),
        ) || 'Неизвестная ошибка',
      )
    },

    onError: (error) => {
      finishCaptchaWait()

      if (isOfflineAuthError(error)) {
        showOfflineAuthMessage()
      }
    },
  })

  const startGetPhoneToCall = () => {
    if (inFlightRef.current) return

    inFlightRef.current = true
    getPhoneToCallMutation(undefined, {
      onSettled: () => {
        inFlightRef.current = false
      },
    })
  }

  return {
    startGetPhoneToCall,
    isGetPhoneToCallPending,
    captcha,
    onCaptchaToken,
    onCaptchaFail,
    onCaptchaRefresh,
  }
}
